import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, X, FileText, UserCheck, UserX, AlertCircle, History, Clock, ChevronRight, Edit3, FileArchive, CreditCard, ClipboardList, CalendarClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getUnreadNotifications,
  getUnseenCount,
  getNotifications,
  markAsRead,
  markAllAsRead,
  markAllAsSeen,
  type Notification,
} from '@/services/notificationService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { RoleRoutes } from '@/types/global.types';

const NOTIFICATION_ICONS: Record<string, React.ReactNode> = {
  PROFILE_SUBMITTED: <FileText className="w-4 h-4 text-amber-500" />,
  PROFILE_APPROVED: <UserCheck className="w-4 h-4 text-green-500" />,
  PROFILE_REJECTED: <UserX className="w-4 h-4 text-red-500" />,
  PROFILE_RESUBMITTED: <FileText className="w-4 h-4 text-blue-500" />,
  DOCUMENT_VERIFIED: <Check className="w-4 h-4 text-green-500" />,
  DOCUMENT_REJECTED: <AlertCircle className="w-4 h-4 text-red-500" />,
  CHANGE_REQUEST_SUBMITTED: <Edit3 className="w-4 h-4 text-amber-500" />,
  CHANGE_REQUEST_APPROVED: <Check className="w-4 h-4 text-green-500" />,
  CHANGE_REQUEST_REJECTED: <X className="w-4 h-4 text-red-500" />,
  EDEP_REPORT_READY: <FileArchive className="w-4 h-4 text-purple-500" />,
  PAYMENT_REMINDER: <CreditCard className="w-4 h-4 text-orange-500" />,
  EXAM_SCHEDULED: <ClipboardList className="w-4 h-4 text-orange-500" />,
  EXAM_UPDATED: <ClipboardList className="w-4 h-4 text-amber-500" />,
  EXAM_CANCELLED: <ClipboardList className="w-4 h-4 text-red-500" />,
  SCHEDULE_CHANGED: <CalendarClock className="w-4 h-4 text-blue-500" />,
  STUDENT_ABSENCE: <UserX className="w-4 h-4 text-red-500" />,
  COURSE_ANNOUNCEMENT: <Bell className="w-4 h-4 text-blue-500" />,
  COURSE_MATERIAL: <FileText className="w-4 h-4 text-purple-500" />,
  COURSE_ASSIGNMENT: <ClipboardList className="w-4 h-4 text-amber-500" />,
};

const NOTIFICATION_COLORS: Record<string, string> = {
  PROFILE_SUBMITTED: 'bg-amber-50 border-amber-200',
  PROFILE_APPROVED: 'bg-green-50 border-green-200',
  PROFILE_REJECTED: 'bg-red-50 border-red-200',
  PROFILE_RESUBMITTED: 'bg-blue-50 border-blue-200',
  DOCUMENT_VERIFIED: 'bg-green-50 border-green-200',
  DOCUMENT_REJECTED: 'bg-red-50 border-red-200',
  CHANGE_REQUEST_SUBMITTED: 'bg-amber-50 border-amber-200',
  CHANGE_REQUEST_APPROVED: 'bg-green-50 border-green-200',
  CHANGE_REQUEST_REJECTED: 'bg-red-50 border-red-200',
  EDEP_REPORT_READY: 'bg-purple-50 border-purple-200',
  PAYMENT_REMINDER: 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200',
  EXAM_SCHEDULED: 'bg-orange-50 border-orange-200',
  EXAM_UPDATED: 'bg-amber-50 border-amber-200',
  EXAM_CANCELLED: 'bg-red-50 border-red-200',
  SCHEDULE_CHANGED: 'bg-blue-50 border-blue-200',
  STUDENT_ABSENCE: 'bg-red-50 border-red-200',
  COURSE_ANNOUNCEMENT: 'bg-blue-50 border-blue-200',
  COURSE_MATERIAL: 'bg-purple-50 border-purple-200',
  COURSE_ASSIGNMENT: 'bg-amber-50 border-amber-200',
};

// Maximum notifications to show in dropdown before showing "See more"
const MAX_NOTIFICATIONS_DISPLAY = 5;

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper to get the students management path based on user role
  const getStudentsManagementPath = (tenantId: string): string => {
    if (!user) return '/login';

    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return '/login';

    const basePath = roleRoute.replace(':tenantId', tenantId);
    return `${basePath}/utilisateurs/etudiants`;
  };

  // Helper to get the staff management path based on user role
  const getStaffManagementPath = (tenantId: string): string => {
    if (!user) return '/login';

    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return '/login';

    const basePath = roleRoute.replace(':tenantId', tenantId);
    return `${basePath}/utilisateurs/personnel`;
  };

  // Helper to get the user's profile path based on their role
  const getProfilePath = (): string => {
    if (!user) return '/login';

    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return '/login';

    const basePath = roleRoute.replace(':tenantId', user.tenantId || '');
    return `${basePath}/profile`;
  };

  // Helper to get the E-DEP reports path based on user role
  const getEdepReportsPath = (tenantId: string): string => {
    if (!user) return '/login';

    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return '/login';

    const basePath = roleRoute.replace(':tenantId', tenantId);
    return `${basePath}/utilisateurs/documents-edep`;
  };

  // Load unseen count on mount and periodically
  useEffect(() => {
    loadUnseenCount();
    const interval = setInterval(loadUnseenCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Load notifications when dropdown opens or showHistory changes
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, showHistory]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnseenCount = async () => {
    try {
      const count = await getUnseenCount();
      setUnseenCount(count);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      if (showHistory) {
        // Load all notifications including read ones
        const data = await getNotifications(50, 0);
        setAllNotifications(data.notifications);
      } else {
        // Load only unread
        const data = await getUnreadNotifications();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBellClick = async () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);

    // Mark all as seen when opening the dropdown
    if (newOpenState && unseenCount > 0) {
      try {
        await markAllAsSeen();
        setUnseenCount(0);
      } catch (error) {
        console.error('Error marking notifications as seen:', error);
      }
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications([]);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    await handleMarkAsRead(notification.id);
    setIsOpen(false);

    // Navigate based on notification type
    switch (notification.type) {
      case 'PROFILE_SUBMITTED':
        // For managers: go to validation page based on user type (relatedType contains user's role)
        if (notification.relatedId && notification.tenantId) {
          // Check if this is a student/parent profile (all other roles go to staff management)
          const isStudentOrParent = notification.relatedType === 'STUDENT' ||
            notification.relatedType === 'PARENT';

          if (isStudentOrParent) {
            // Student or Parent - go to student management
            const studentsPath = getStudentsManagementPath(notification.tenantId);
            navigate(`${studentsPath}?validation=${notification.relatedId}`);
          } else {
            // Staff/Teacher profiles - go to personnel management
            const staffPath = getStaffManagementPath(notification.tenantId);
            navigate(`${staffPath}?validation=${notification.relatedId}`);
          }
        }
        break;
      case 'PROFILE_APPROVED':
      case 'PROFILE_REJECTED':
        // For users: go to their profile page with correct role-based path
        navigate(getProfilePath());
        break;
      case 'CHANGE_REQUEST_SUBMITTED':
        // For managers: go to appropriate management page based on the user type who submitted
        // relatedType in this case contains 'ProfileChangeRequest', but the user role info may be in the notification
        // We need to check if this is for staff or student - default to staff management for change requests
        if (notification.tenantId) {
          // Check if the notification message contains student-related keywords
          const isStudentRelated = notification.message?.toLowerCase().includes('étudiant') ||
            notification.message?.toLowerCase().includes('student') ||
            notification.relatedType === 'STUDENT' ||
            notification.relatedType === 'PARENT';

          if (isStudentRelated) {
            const studentsPath = getStudentsManagementPath(notification.tenantId);
            navigate(`${studentsPath}?showChangeRequests=true`);
          } else {
            // Staff change request - go to personnel management
            const staffPath = getStaffManagementPath(notification.tenantId);
            navigate(`${staffPath}?showChangeRequests=true`);
          }
        }
        break;
      case 'CHANGE_REQUEST_APPROVED':
      case 'CHANGE_REQUEST_REJECTED':
        // For users: go to their profile page with correct role-based path
        navigate(getProfilePath());
        break;
      case 'EDEP_REPORT_READY':
        // Go to E-DEP reports page with the appropriate tab selected
        if (notification.tenantId) {
          const edepPath = getEdepReportsPath(notification.tenantId);
          // Determine if it's teachers or staff based on the notification message
          const isTeachers = notification.message?.toLowerCase().includes('enseignant');
          navigate(`${edepPath}?tab=${isTeachers ? 'TEACHERS' : 'ADMIN_STAFF'}`);
        }
        break;
      case 'PAYMENT_REMINDER':
        // Route each role to its actual finance page. The student page lives at
        // `/compte-financier`; the parent page renders directly at `/payments`.
        // (Navigating a student to `/payments` used to hit a relative redirect
        // that resolved to `/payments/compte-financier` → 404.)
        if (user && user.tenantId) {
          const roleRoute = RoleRoutes[user.role];
          if (roleRoute) {
            const basePath = roleRoute.replace(':tenantId', user.tenantId);
            const financePath = user.role === 'STUDENT' ? 'compte-financier' : 'payments';
            navigate(`${basePath}/${financePath}`);
          }
        }
        break;
      case 'COURSE_ANNOUNCEMENT':
      case 'COURSE_MATERIAL':
      case 'COURSE_ASSIGNMENT':
        // Course content posted by the professor: open the course page
        // (relatedId = moduleElementId)
        if (user && user.tenantId && notification.relatedId) {
          const roleRoute = RoleRoutes[user.role];
          if (roleRoute) {
            const tab =
              notification.type === 'COURSE_MATERIAL'
                ? 'content'
                : notification.type === 'COURSE_ASSIGNMENT'
                  ? 'assignments'
                  : 'announcements';
            navigate(
              `${roleRoute.replace(':tenantId', user.tenantId)}/mes-cours/${notification.relatedId}?tab=${tab}`,
            );
          }
        }
        break;
      case 'STUDENT_ABSENCE':
        // Parent absence alert: go to the Présences page
        if (user && user.tenantId) {
          const roleRoute = RoleRoutes[user.role];
          if (roleRoute) {
            navigate(`${roleRoute.replace(':tenantId', user.tenantId)}/presences`);
          }
        }
        break;
      case 'SCHEDULE_CHANGED':
        // Schedule change: student -> own timetable, parent -> child timetable
        if (user && user.tenantId) {
          const roleRoute = RoleRoutes[user.role];
          if (roleRoute) {
            const basePath = roleRoute.replace(':tenantId', user.tenantId);
            navigate(user.role === 'STUDENT' ? `${basePath}/mon-emploi-du-temps` : `${basePath}/emploi-du-temps`);
          }
        }
        break;
      case 'EXAM_SCHEDULED':
      case 'EXAM_UPDATED':
      case 'EXAM_CANCELLED':
        // Exam notifications: each panel exposes its exam planning at a different path
        if (user && user.tenantId) {
          const roleRoute = RoleRoutes[user.role];
          if (roleRoute) {
            const basePath = roleRoute.replace(':tenantId', user.tenantId);
            if (user.role === 'STUDENT' || user.role === 'PARENT') {
              navigate(`${basePath}/planning-examens`);
            } else if (user.role === 'TEACHER') {
              // Teachers see their exam surveillances in their timetable
              navigate(`${basePath}/mon-emploi-du-temps`);
            } else {
              navigate(`${basePath}/calendrier/examens`);
            }
          }
        }
        break;
      default:
        // Default: go to profile with correct role-based path
        navigate(getProfilePath());
    }
  };

  const handleSeeMore = () => {
    setIsOpen(false);
    // Navigate to a dedicated notifications page (you could create one)
    // For now, just switch to history mode
    setShowHistory(true);
    setIsOpen(true);
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;

    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatFullDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get the notifications to display based on mode
  const displayedNotifications = showHistory ? allNotifications : notifications;
  const limitedNotifications = displayedNotifications.slice(0, MAX_NOTIFICATIONS_DISPLAY);
  const hasMore = displayedNotifications.length > MAX_NOTIFICATIONS_DISPLAY;
  const remainingCount = displayedNotifications.length - MAX_NOTIFICATIONS_DISPLAY;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
            {unseenCount > 99 ? '99+' : unseenCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-900">
              Notifications
              {!showHistory && notifications.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({notifications.length} non lue{notifications.length > 1 ? 's' : ''})
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {!showHistory && notifications.length > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                  title="Tout marquer comme lu"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`flex items-center gap-1 text-sm px-2 py-1 rounded-md transition-colors ${
                  showHistory
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title={showHistory ? 'Voir non lues' : 'Voir l\'historique'}
              >
                <History className="w-4 h-4" />
                {showHistory ? 'Non lues' : 'Historique'}
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : displayedNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Bell className="w-8 h-8 mb-2 text-gray-300" />
                <p>{showHistory ? 'Aucune notification dans l\'historique' : 'Aucune notification non lue'}</p>
              </div>
            ) : (
              <>
                {limitedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                      notification.isRead ? 'bg-white opacity-75' : NOTIFICATION_COLORS[notification.type] || 'bg-white'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {NOTIFICATION_ICONS[notification.type] || <Bell className="w-4 h-4 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${notification.isRead ? 'text-gray-600' : 'text-gray-900'}`}>
                            {notification.title}
                          </p>
                          {notification.isRead && (
                            <span className="text-xs text-gray-400 flex-shrink-0">Lu</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <p className="text-xs text-gray-400">
                            {showHistory ? formatFullDateTime(notification.createdAt) : formatTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                          title="Marquer comme lu"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer with "See more" link */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
            {hasMore && !showHistory ? (
              <button
                onClick={handleSeeMore}
                className="w-full flex items-center justify-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Voir plus de notifications ({remainingCount})
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <p className="text-xs text-gray-500 text-center">
                {showHistory
                  ? `${displayedNotifications.length} notification(s) dans l'historique`
                  : notifications.length > 0
                    ? 'Cliquez sur une notification pour la marquer comme lue'
                    : 'Cliquez sur "Historique" pour voir les anciennes notifications'
                }
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
